/**
 * Zod schemas for the Content Writer research pipeline.
 *
 * Owned by `prompts/02-features/content-writer/01-research-pipeline-backend-opus-4.7.md`.
 *
 * Two surfaces share these schemas:
 *  - tRPC router input validation (`apps/api/src/routers/research.ts`)
 *  - Backend service layer that runs research + generation
 *
 * Mirrors the `draft_status` and `platform` Postgres enums in
 * `packages/db/src/enums.ts`. Keep all enum values in sync — the AI generator
 * may emit `metadata.platform` and the validator must accept identical values.
 */

import { z } from "zod";

export const contentWriterPlatformEnum = z.enum([
  "linkedin",
  "twitter",
  "instagram",
  "youtube",
  "threads",
  "tiktok",
]);
export type ContentWriterPlatform = z.infer<typeof contentWriterPlatformEnum>;

export const draftStatusSchema = z.enum([
  "draft",
  "approved",
  "scheduled",
  "published",
  "discarded",
]);
export type DraftStatus = z.infer<typeof draftStatusSchema>;

export const researchTypeSchema = z.enum(["topic", "url", "hashtag"]);
export type ResearchType = z.infer<typeof researchTypeSchema>;

export const researchInputSchema = z.object({
  query: z.string().min(1).max(500),
  type: researchTypeSchema,
  platforms: z.array(contentWriterPlatformEnum).min(1).max(6),
  brandProfileId: z.string().min(1).optional(),
  workspaceId: z.string().min(1),
});
export type ResearchInput = z.infer<typeof researchInputSchema>;

export const generateDraftInputSchema = z.object({
  researchId: z.string().min(1),
  platform: contentWriterPlatformEnum,
  brandProfileId: z.string().min(1),
});
export type GenerateDraftInput = z.infer<typeof generateDraftInputSchema>;

export const regenerateInputSchema = z.object({
  draftId: z.string().min(1),
  instructions: z.string().min(1).max(1000),
});
export type RegenerateInput = z.infer<typeof regenerateInputSchema>;

export const draftFiltersSchema = z.object({
  workspaceId: z.string().min(1),
  status: draftStatusSchema.optional(),
  platform: contentWriterPlatformEnum.optional(),
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(100).default(20),
});
export type DraftFilters = z.infer<typeof draftFiltersSchema>;

export const researchSourceSchema = z.object({
  url: z.string().url(),
  title: z.string(),
  snippet: z.string(),
  relevanceScore: z.number().min(0).max(1),
  publishedDate: z.string().optional(),
});
export type ResearchSource = z.infer<typeof researchSourceSchema>;

export const youtubeTimestampSchema = z.object({
  time: z.string(),
  label: z.string(),
});

export const contentDraftMetadataSchema = z
  .object({
    threadParts: z.array(z.string()).optional(),
    tags: z.array(z.string()).optional(),
    timestamps: z.array(youtubeTimestampSchema).optional(),
  })
  .optional();

export const contentDraftOutputSchema = z.object({
  content: z.string().min(1),
  hashtags: z.array(z.string()).max(40).optional(),
  characterCount: z.number().int().min(0),
  platform: contentWriterPlatformEnum,
  metadata: contentDraftMetadataSchema,
});
export type ContentDraftOutput = z.infer<typeof contentDraftOutputSchema>;

export const researchProgressStatusSchema = z.enum([
  "researching",
  "parsing",
  "compiling",
  "generating",
  "complete",
  "failed",
]);
export type ResearchProgressStatus = z.infer<typeof researchProgressStatusSchema>;
